import React from 'react';
import PropTypes from 'prop-types';
import { isNumber, isEmpty, sortBy, reduce, filter, find, findIndex } from 'lodash';
import Modal from 'react-modal';
import hash from 'object-hash';
import ModalB from 'components/Modal/ModalB';
import BuildChildren from 'utils/BuildChildren';
import { priceFormatter } from 'utils/Numbers';
import classNames from 'classnames';
import classes from './CardProductRadio.module.scss';

const CardProductRadio = (props) => {
  const {
    items = [],
    selectedOptionIndex,
    modalOpt,
    isOpen,
    click,
    toggleModal,
    onAfterClose,
    itemType,
    gridClass,
    groupByCategory = false,
  } = props;

  const sIndex = (isNumber(selectedOptionIndex) && selectedOptionIndex >= 0 && selectedOptionIndex) || 0;
  const selectedOption = items[sIndex];
  const selectedButton = selectedOption && selectedOption.button;
  const selectedButtonDisabled = selectedButton &&
    (selectedButton.disabled || (!isNumber(selectedOptionIndex) || selectedOptionIndex < 0));
  const infoLine1 = selectedOption &&
    `You selected ${selectedOption.displayName} for ${priceFormatter(selectedOption.price)}.`;
  const infoLine2 = selectedButton && selectedButtonDisabled ? 'This option is not available'
    : `Click ${selectedButton ? selectedButton.title : ''} to finish the checkout process.`;
  const itemsHash = hash(items);
  // const TypeComponent = Components[itemType];

  const packageTabCategoryList = sortBy(reduce(items, (acc, item) => {
    if (!isEmpty(item.packageTabProductCategory) && !find(acc, item.packageTabProductCategory)) {
      acc.push(item.packageTabProductCategory);
    }
    return acc;
  }, []), ['ordinal', 'title']);

  const nonCategorizedItems = filter(items, (it) => isEmpty(it.packageTabProductCategory));

  const renderItems = (itemsArray, category = 'uncategorized') => (
    <React.Fragment key={`items-${category}`}>
      {!isEmpty(itemsArray) && itemsArray.map((option) => {
        const index = findIndex(
          items,
          (i) => (i.productId === option.productId && i.displayName === option.displayName),
        );
        return BuildChildren({
          type: itemType,
          product: option,
          productIndex: index,
          isSelected: selectedOptionIndex === index,
          onClick: click,
          showDetails: (e) => toggleModal(e, index, true),
          key: `items-${category}-${index}`,
          hash: itemsHash,
          gridClass,
        });
      })}
    </React.Fragment>
  );

  const renderGroupedItems = () => (
    <React.Fragment key='group-items'>
      {!isEmpty(nonCategorizedItems) && renderItems(nonCategorizedItems)}
      {!isEmpty(packageTabCategoryList) && packageTabCategoryList.map((category) => {
        const itemsPerCategory = filter(items, (it) => (
          it.packageTabProductCategory &&
            it.packageTabProductCategory.ordinal === category.ordinal &&
            it.packageTabProductCategory.title === category.title
        ));

        return (
          <React.Fragment key={`category-${category.title}`}>
            <h5 className={classes.categoryTitle}>{category.title}</h5>
            {!isEmpty(itemsPerCategory) && renderItems(itemsPerCategory, category.title)}
          </React.Fragment>
        );
      })}
    </React.Fragment>
  );

  const renderUngroupedItems = () => !isEmpty(items) && renderItems(items);

  return (
    <div className={classes.wrapper}>
      <div className={classes.container}>
        <div className={classes.infoContainer}>
          {selectedOptionIndex >= 0 &&
            <div
              className={classes.hidden}
              aria-live='polite'
              aria-atomic='true'
              aria-relevant='additions'
            >
              {`${infoLine1} ${infoLine2}`}
            </div>}
          {groupByCategory ? renderGroupedItems() : renderUngroupedItems()}
          {selectedButton &&
            <div className={classNames(classes.button, classes[`buttonFor${itemType}`])}>
              {BuildChildren({
                ...selectedButton,
                disabled: selectedButtonDisabled,
              })}
            </div>}
        </div>
        <Modal
          isOpen={isOpen}
          onAfterClose={onAfterClose}
          onRequestClose={(e) => toggleModal(e, undefined, false)}
          shouldCloseOnOverlayClick={false}
          ariaHideApp={false}
          className={classes.modal}
          overlayClassName={classes.overlayModal}
          closeTimeoutMS={250}
        >
          <ModalB hideModal={(e) => toggleModal(e, undefined, false)}>
            { isNumber(modalOpt) && modalOpt >= 0 && items[modalOpt] &&
              <div
                className={classes.description}
                dangerouslySetInnerHTML={{ __html: items[modalOpt].description }}
              />}
          </ModalB>
        </Modal>
      </div>
    </div>
  );
};

CardProductRadio.propTypes = {
  /**
   * Array of products
  */
  items: PropTypes.array.isRequired,
  /**
   * Index of the selected product in the list
  */
  selectedOptionIndex: PropTypes.number,
  /**
   * Index of the product you clickec to expand its detail
  */
  modalOpt: PropTypes.number,
  /**
   * Boolean to tell if the modal is open or not with the product details<br>
   * Options: true or false
  */
  isOpen: PropTypes.bool,
  /**
   * Callback when clicking the product
  */
  click: PropTypes.func,
  /**
   * Callback when expanding the details of the product
  */
  toggleModal: PropTypes.func,
  /**
   * Callback when expanding the details of the product
  */
  onAfterClose: PropTypes.func,
  /**
   * Type of item to use in the list<br>
   * Options: CardProductItemA or CardProductItemD
  */
  itemType: PropTypes.string,
  /**
   * Grid css class being used in the PackageTabB widget
   * Options: grid2Cols, grid3Cols, grid4Cols, grid5Cols, grid6Cols
  */
  gridClass: PropTypes.string,
  /**
   * Boolean to tell whether we should group by category the list of items<br>
   * Items without categories will display first and then we will sort categories using the data from sitefinity
  */
  groupByCategory: PropTypes.bool,
};

export default CardProductRadio;
